import { flowiseService } from "../../agent/services/FlowiseService";
import { storageService } from "../../../services/storageService";
import mastitisData from "../../../../info-mastitis.json";
import { getTreatmentByOrganism } from "./mastitisTreatment";

export interface MastitisTestData {
  animalId?: string;
  teats?: string[];
  plateImageFile?: File;
  collectionDate?: string;
  selectionsPrimary?: {
    staphylococcus?: string;
    geral?: string;
    streptococcus?: string;
  };
  selectionsSecondary?: {
    staphylococcus?: string;
    geral?: string;
    streptococcus?: string;
  };
  clinical?: {
    milkCondition?: string;
    udderConditions?: string[];
    severity?: string;
  };
  finalNotes?: string;
}

export interface MastitisTestPayload {
  user_id: string;
  business_unit_type: string;
  business_unit_id: string;
  question: string;
  insertExam: string;
  examType: string;
  cattleTag: string;
  collectionDate: string;
  examDate: string;
  attachments: string;
  examData: string;
  plateId: string;
  examResult: string;
}

export class MastitisTestService {
  /**
   * Converte nome da imagem para nome do microorganismo
   */
  private static getOrganismNameFromImage(imageName: string): string {
    if (!imageName) return "";

    // Procura o microorganismo correspondente à imagem
    for (const result of mastitisData.ResultsMastitis) {
      if (
        result.ImagemCromogenicoStaphylococcus === imageName ||
        result.ImagemCromogenicoGeral === imageName ||
        result.ImagemCromogenicoStreptococcus === imageName
      ) {
        return result.Microrganismo;
      }
    }

    // Se não encontrar, retorna o nome da imagem sem extensão como fallback
    return imageName.replace(".png", "").replace(/-/g, " ");
  }

  /**
   * Constrói o payload para o gestor laboratorial baseado nos dados do teste de mastite
   */
  static buildMastitisPayload(
    testData: MastitisTestData,
    userId: string,
    businessUnitId: string,
    sessionId: string
  ): any {
    // Determina o resultado do exame baseado nas seleções
    const examResult = this.determineExamResult(testData);
    const treatmentPlan = getTreatmentByOrganism(examResult);

    // Função para formatar data no padrão YYYY-MM-DD
    const formatDate = (dateString?: string): string => {
      if (dateString) {
        // Se já está no formato correto, retorna como está
        if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
          return dateString;
        }
        // Tenta converter outros formatos
        const date = new Date(dateString);
        if (!isNaN(date.getTime())) {
          return date.toISOString().split("T")[0];
        }
      }
      // Fallback para data atual no formato correto
      return new Date().toISOString().split("T")[0];
    };

    // Processa milkAspect como array se for string
    const processMilkAspect = (milkCondition?: string): string[] => {
      if (!milkCondition) return [];
      if (Array.isArray(milkCondition)) return milkCondition;
      // Se for string, converte para array
      return [milkCondition];
    };

    // Constrói o examData como JSON string, convertendo nomes de imagens para nomes de microorganismos
    const examData = {
      staphPrimary: this.getOrganismNameFromImage(
        testData.selectionsPrimary?.staphylococcus || ""
      ),
      staphSecondary: this.getOrganismNameFromImage(
        testData.selectionsSecondary?.staphylococcus || ""
      ),
      strepPrimary: this.getOrganismNameFromImage(
        testData.selectionsPrimary?.streptococcus || ""
      ),
      strepSecondary: this.getOrganismNameFromImage(
        testData.selectionsSecondary?.streptococcus || ""
      ),
      generalPrimary: this.getOrganismNameFromImage(
        testData.selectionsPrimary?.geral || ""
      ),
      generalSecondary: this.getOrganismNameFromImage(
        testData.selectionsSecondary?.geral || ""
      ),
      milkAspect: processMilkAspect(testData.clinical?.milkCondition),
      udderSigns: testData.clinical?.udderConditions || [],
      teatsAffected: testData.teats || [],
      observation: testData.finalNotes || "",
      treatmentPlan,
    };

    const collectionDate = formatDate(testData.collectionDate);
    const examDate = formatDate(); // Data atual para examDate

    // Constrói o payload no formato esperado pelo gestor laboratorial
    const payload = {
      form: {
        user_id: userId,
        business_unit_type: "farm",
        business_unit_id: businessUnitId,
        question: "",
        insertExam: "true",
        examType: "mastitis_test",
        cattleTag: testData.animalId || "",
        collectionDate: collectionDate,
        examDate: examDate,
        attachments: JSON.stringify([
          {
            type: "image",
            url: testData.plateImageFile ? "plate_image" : "",
          },
        ]),
        examData: JSON.stringify(examData),
        plateId: testData.animalId || "",
        examResult: examResult,
      },
      streaming: false,
      overrideConfig: {
        sessionId: sessionId,
        vars: {
          user_id: userId,
          business_unit_type: "farm",
          business_unit_id: businessUnitId,
          question: "",
          insertExam: "true",
          examType: "mastitis_test",
          cattleTag: testData.animalId || "",
          collectionDate: collectionDate,
          examDate: examDate,
          attachments: JSON.stringify([
            {
              type: "image",
              url: testData.plateImageFile ? "plate_image" : "",
            },
          ]),
          examData: JSON.stringify(examData),
          plateId: testData.animalId || "",
          examResult: examResult,
        },
      },
    };

    return payload;
  }

  /**
   * Determina o resultado do exame baseado nas seleções do usuário
   */
  private static determineExamResult(testData: MastitisTestData): string {
    const primary = testData.selectionsPrimary;

    // Usar a mesma lógica do matchFor() do MastitisTestStep3
    const result = this.matchFor(primary);
    
    if (result?.Microrganismo) {
      return result.Microrganismo;
    }

    // Fallback para severidade clínica se não encontrar match
    const clinical = testData.clinical;
    if (clinical?.severity === "grave") {
      return "Mastite clínica grave";
    }

    if (clinical?.severity === "medio") {
      return "Mastite clínica moderada";
    }

    return "Resultado inconclusivo";
  }

  /**
   * Implementa a mesma lógica do matchFor() usado na tela de diagnóstico
   */
  private static matchFor(sel?: any) {
    if (!sel) return null;
    
    const staph = sel?.staphylococcus;
    const geral = sel?.geral;
    const strep = sel?.streptococcus;
    
    const pairs: Array<{ key: string; val?: string | null }> = [
      { key: "ImagemCromogenicoStaphylococcus", val: staph },
      { key: "ImagemCromogenicoGeral", val: geral },
      { key: "ImagemCromogenicoStreptococcus", val: strep },
    ];
    
    const active = pairs.filter((p) => Boolean(p.val));
    if (active.length === 0) return null;

    let best: any = null;
    let bestScore = -1;
    
    for (const item of mastitisData.ResultsMastitis) {
      let score = 0;
      for (const p of active) {
        if ((item as any)?.[p.key] === p.val) score += 1;
      }
      if (score > bestScore) {
        bestScore = score;
        best = item;
      }
    }
    
    return best;
  }

  /**
   * Envia os dados do teste de mastite para o backend através do gestor laboratorial
   */
  static async sendMastitisTest(
    testData: MastitisTestData,
    userId: string,
    businessUnitId: string,
    sessionId: string
  ): Promise<{ success: boolean; response?: any; error?: string }> {
    try {
      // Upload da imagem da placa (se disponível) para bucket de exames
      let attachmentObjectKey: string | undefined;
      if (testData.plateImageFile) {
        try {
          const uploadRes = await storageService.uploadExamImage({
            farmId: businessUnitId,
            animalName: testData.animalId || "animal",
            file: testData.plateImageFile,
            contentType: (testData.plateImageFile as any)?.type || "image/jpeg",
            bucket: (import.meta as any)?.env?.VITE_S3_BUCKET_EXAMES || "",
          });
          // Enviar diretamente o objectKey para o Flowise (não buscar URL)
          attachmentObjectKey = uploadRes.objectKey;
        } catch (e) {
          console.error('Falha ao enviar imagem do exame para o bucket exames:', e);
        }
      }

      // Constrói o payload
      const payload = this.buildMastitisPayload(
        testData,
        userId,
        businessUnitId,
        sessionId
      );

      // Determina o resultado do exame
      const examResult = this.determineExamResult(testData);
      const treatmentPlan = getTreatmentByOrganism(examResult);

      // Envia através do FlowiseService usando o gestor laboratorial
      const result = await flowiseService.processPermissionsMessage({
        owner_id: userId,
        business_unit_type: "farm",
        business_unit_id: businessUnitId,
        manager_type: "laboratory",
        question: "",
        sessionId: sessionId,
        insertExam: "true",
        examType: "mastitis_test",
        cattleTag: testData.animalId || "",
        collectionDate:
          testData.collectionDate || new Date().toLocaleDateString("pt-BR"),
        examDate: new Date().toLocaleDateString("pt-BR"),
        // Enviar objectKey diretamente como valor de url
        attachments: attachmentObjectKey
          ? [{ type: "image", url: attachmentObjectKey }]
          : [],
        examData: {
          // Enviar apenas o nome do microrganismo correspondente às imagens selecionadas
          staphPrimary: MastitisTestService.getOrganismNameFromImage(
            testData.selectionsPrimary?.staphylococcus || ""
          ),
          staphSecondary: MastitisTestService.getOrganismNameFromImage(
            testData.selectionsSecondary?.staphylococcus || ""
          ),
          strepPrimary: MastitisTestService.getOrganismNameFromImage(
            testData.selectionsPrimary?.streptococcus || ""
          ),
          strepSecondary: MastitisTestService.getOrganismNameFromImage(
            testData.selectionsSecondary?.streptococcus || ""
          ),
          generalPrimary: MastitisTestService.getOrganismNameFromImage(
            testData.selectionsPrimary?.geral || ""
          ),
          generalSecondary: MastitisTestService.getOrganismNameFromImage(
            testData.selectionsSecondary?.geral || ""
          ),
          // milkAspect deve ser um array de strings
          milkAspect: Array.isArray(testData.clinical?.milkCondition)
            ? (testData.clinical?.milkCondition as string[])
            : (testData.clinical?.milkCondition
                ? [testData.clinical?.milkCondition]
                : []),
          udderSigns: testData.clinical?.udderConditions || [],
          teatsAffected: testData.teats || [],
          observation: testData.finalNotes || "",
          treatmentPlan: getTreatmentByOrganism(examResult),
        },
        plateId: testData.animalId || "",
        examResult: examResult,
      });

      if (result.success) {
        return {
          success: true,
          response: result.response,
        };
      } else {
        return {
          success: false,
          error: result.error || "Erro desconhecido ao enviar teste de mastite",
        };
      }
    } catch (error) {
      console.error("Erro ao enviar teste de mastite:", error);
      return {
        success: false,
        error: error instanceof Error ? error.message : "Erro desconhecido",
      };
    }
  }

  /**
   * Formata os dados do teste para exibição
   */
  static formatTestDataForDisplay(testData: MastitisTestData): string {
    const parts: string[] = [];

    if (testData.animalId) {
      parts.push(`Animal: ${testData.animalId}`);
    }

    if (testData.teats && testData.teats.length > 0) {
      parts.push(`Tetos afetados: ${testData.teats.join(", ")}`);
    }

    if (testData.clinical?.milkCondition) {
      parts.push(`Aspecto do leite: ${testData.clinical.milkCondition}`);
    }

    if (
      testData.clinical?.udderConditions &&
      testData.clinical.udderConditions.length > 0
    ) {
      parts.push(
        `Sinais no úbere: ${testData.clinical.udderConditions.join(", ")}`
      );
    }

    if (testData.finalNotes) {
      parts.push(`Observações: ${testData.finalNotes}`);
    }

    return parts.join("\n");
  }
}
